// 文書の状態モデル。
//
// ここに定義する型はすべて JSON シリアライズ可能でなければならない
// (project ZIP の document.json にそのまま書き出し、履歴にも保持するため)。
// ピクセルデータ (タイル / ImageData / GPU テクスチャ)、Blob、Object URL は
// 絶対にここへ入れない。ピクセルは pixels/pixelStore.ts の非リアクティブな
// Map に置き、Document からは pixelId で参照するだけにする。
//
// 座標・サイズはすべて文書ピクセル。表示ズームは含めない。

import type { Pt, Mat3, Rect } from './geometry'

export type LayerId = string
export type PixelId = string

/** Photoshop の 27 種 + グループ専用の pass-through。順序は UI のメニュー順。 */
export const BLEND_MODES = [
  'normal',
  'dissolve',
  'darken',
  'multiply',
  'color-burn',
  'linear-burn',
  'darker-color',
  'lighten',
  'screen',
  'color-dodge',
  'linear-dodge',
  'lighter-color',
  'overlay',
  'soft-light',
  'hard-light',
  'vivid-light',
  'linear-light',
  'pin-light',
  'hard-mix',
  'difference',
  'exclusion',
  'subtract',
  'divide',
  'hue',
  'saturation',
  'color',
  'luminosity'
] as const

export type BlendMode = (typeof BLEND_MODES)[number]
export type GroupBlendMode = BlendMode | 'pass-through'

export interface LayerLock {
  all: boolean
  pixels: boolean
  position: boolean
  transparency: boolean
}

export interface LayerMask {
  pixelId: PixelId
  enabled: boolean
  linked: boolean
  /** 0..1。マスクの効き具合 (Photoshop の「濃度」) */
  density: number
  /** ぼかし半径 (px) */
  feather: number
  offset: Pt
}

/** レイヤースタイル (Photoshop のレイヤー効果)。各効果は enabled を持つ */
export interface ShadowFx {
  enabled: boolean
  blendMode: BlendMode
  hex: string
  opacity: number
  /** 光源角度 (度、0 = 右、反時計回り) */
  angle: number
  distance: number
  /** スプレッド / チョーク 0..100 */
  spread: number
  size: number
}
export interface GlowFx {
  enabled: boolean
  blendMode: BlendMode
  hex: string
  opacity: number
  spread: number
  size: number
  /** 内側光彩のみ: 中央から / エッジから */
  source?: 'edge' | 'center'
}
export interface BevelFx {
  enabled: boolean
  style: 'inner' | 'outer' | 'emboss'
  depth: number
  size: number
  soften: number
  angle: number
  altitude: number
  highlightHex: string
  highlightOpacity: number
  shadowHex: string
  shadowOpacity: number
}
export interface SatinFx {
  enabled: boolean
  blendMode: BlendMode
  hex: string
  opacity: number
  angle: number
  distance: number
  size: number
  invert: boolean
}
export interface OverlayFx {
  enabled: boolean
  blendMode: BlendMode
  opacity: number
  /** 単色 */
  hex: string
  /** グラデーション (gradientOverlay のみ) */
  stops?: { pos: number; hex: string }[]
  angle?: number
  style?: 'linear' | 'radial'
}
export interface StrokeFx {
  enabled: boolean
  size: number
  position: 'outside' | 'inside' | 'center'
  blendMode: BlendMode
  opacity: number
  hex: string
}
export interface PatternFx {
  enabled: boolean
  blendMode: BlendMode
  opacity: number
  patternId: string
  /** 倍率 0.05..10 */
  scale: number
}
export interface LayerStyle {
  dropShadow?: ShadowFx
  innerShadow?: ShadowFx
  outerGlow?: GlowFx
  innerGlow?: GlowFx
  bevel?: BevelFx
  satin?: SatinFx
  colorOverlay?: OverlayFx
  gradientOverlay?: OverlayFx
  patternOverlay?: PatternFx
  stroke?: StrokeFx
}

/**
 * ブレンド条件 (Photoshop の「ブレンド条件」)。各 4 値は 0..255 の [黒側 開始, 黒側 終了, 白側 開始, 白側 終了]。
 * 開始と終了が違えばその間で滑らかに混ざる。
 */
export interface BlendIf {
  channel: 'gray' | 'r' | 'g' | 'b'
  thisLayer: [number, number, number, number]
  underlying: [number, number, number, number]
}

export interface LayerBase {
  id: LayerId
  name: string
  visible: boolean
  /** 0..1 */
  opacity: number
  /** 0..1。レイヤー本体だけに掛かり、レイヤースタイルには掛からない */
  fillOpacity: number
  lock: LayerLock
  /** 直下のレイヤーでクリッピングする */
  clipToBelow: boolean
  mask?: LayerMask
  labelColor?: string | null
  styles?: LayerStyle
  blendIf?: BlendIf
}

/**
 * ラスターレイヤー。ピクセル表面はレイヤー固有の原点を持ち、文書座標 =
 * 表面座標 + offset。移動は offset を変えるだけでピクセル履歴を作らない。
 * カンバス外のピクセルも保持する (Photoshop と同じ)。
 */
export interface RasterLayer extends LayerBase {
  kind: 'raster'
  blendMode: BlendMode
  pixelId: PixelId
  offset: Pt
}

export interface GroupLayer extends LayerBase {
  kind: 'group'
  blendMode: GroupBlendMode
  /** index 0 が最下層 (Photoshop と同じ) */
  children: LayerId[]
  expanded: boolean
}

/** 1 チャンネル分のレベル補正 */
export interface LevelsChannel {
  inBlack: number
  inWhite: number
  gamma: number
  outBlack: number
  outWhite: number
}
/** トーンカーブの制御点 (0..255、x 昇順) */
export type CurvePoints = [number, number][]

export interface HslShift {
  hue: number
  saturation: number
  lightness: number
}

export type SelectiveColorRange = 'reds' | 'yellows' | 'greens' | 'cyans' | 'blues' | 'magentas' | 'whites' | 'neutrals' | 'blacks'
export const SELECTIVE_COLOR_RANGES: SelectiveColorRange[] = ['reds', 'yellows', 'greens', 'cyans', 'blues', 'magentas', 'whites', 'neutrals', 'blacks']

export type Adjustment =
  | { kind: 'levels'; rgb: LevelsChannel; r?: LevelsChannel; g?: LevelsChannel; b?: LevelsChannel }
  | { kind: 'curves'; rgb: CurvePoints; r?: CurvePoints; g?: CurvePoints; b?: CurvePoints }
  | { kind: 'brightness-contrast'; brightness: number; contrast: number }
  | { kind: 'exposure'; exposure: number; offset: number; gamma: number }
  | { kind: 'hue-saturation'; master: HslShift; colorize: boolean; colorizeHue: number; colorizeSaturation: number; colorizeLightness: number }
  | { kind: 'color-balance'; shadows: [number, number, number]; midtones: [number, number, number]; highlights: [number, number, number]; preserveLuminosity: boolean }
  | { kind: 'black-white'; reds: number; yellows: number; greens: number; cyans: number; blues: number; magentas: number; tint: boolean; tintHex: string }
  | { kind: 'photo-filter'; hex: string; density: number; preserveLuminosity: boolean }
  | { kind: 'channel-mixer'; r: [number, number, number, number]; g: [number, number, number, number]; b: [number, number, number, number]; monochrome: boolean }
  | { kind: 'invert' }
  | { kind: 'posterize'; levels: number }
  | { kind: 'threshold'; level: number }
  | { kind: 'gradient-map'; stops: { pos: number; hex: string }[]; reverse: boolean }
  | { kind: 'vibrance'; vibrance: number; saturation: number }
  | { kind: 'selective-color'; ranges: Record<SelectiveColorRange, [number, number, number, number]>; relative: boolean }
  /** カラールックアップ (3D LUT)。lutPixelId はタイル化した LUT 画像 (幅 size², 高さ size) の表面。null なら恒等 */
  | { kind: 'color-lookup'; lutPixelId: PixelId | null; size: number; name: string }

export type AdjustmentKind = Adjustment['kind']

export interface AdjustmentLayer extends LayerBase {
  kind: 'adjustment'
  blendMode: BlendMode
  adjustment: Adjustment
}

export type FillPaint =
  | { kind: 'solid'; hex: string }
  | { kind: 'gradient'; style: 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond'; stops: { pos: number; hex: string; alpha: number }[]; angle: number; scale: number; reverse: boolean }
  | { kind: 'pattern'; patternId: string; scale: number; offset: Pt }

export interface FillLayer extends LayerBase {
  kind: 'fill'
  blendMode: BlendMode
  fill: FillPaint
}

export interface TextStyle {
  fontFamily: string
  fontSize: number
  fontWeight: number
  italic: boolean
  hex: string
  /** 行送り (フォントサイズに対する倍率) */
  lineHeight: number
  /** 字間 (px) */
  letterSpacing: number
  align: 'left' | 'center' | 'right'
  orientation: 'horizontal' | 'vertical'
}

export type WarpStyle = 'arc' | 'arcLower' | 'arcUpper' | 'bulge' | 'flag' | 'wave' | 'fisheye' | 'rise'
/** ワープテキスト (Photoshop のワープ)。bend / hDist / vDist は -100..100 */
export interface TextWarp {
  style: WarpStyle
  bend: number
  hDist: number
  vDist: number
}

export interface TextLayer extends LayerBase {
  kind: 'text'
  blendMode: BlendMode
  text: string
  style: TextStyle
  /** 文書座標。横書きは 1 行目のベースライン左端 (align に応じた基準)、縦書きは 1 列目の上端中央 */
  position: Pt
  /** 段落テキストの幅 (px)。無ければポイントテキスト (改行のみ) */
  boxWidth?: number
  warp?: TextWarp
}

export type ShapeGeometry =
  | { type: 'rect'; x: number; y: number; w: number; h: number; radius: number }
  | { type: 'ellipse'; x: number; y: number; w: number; h: number }
  | { type: 'polygon'; x: number; y: number; w: number; h: number; sides: number; star: number }
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { type: 'path'; points: { x: number; y: number; in?: Pt; out?: Pt }[]; closed: boolean }

export interface ShapeLayer extends LayerBase {
  kind: 'shape'
  blendMode: BlendMode
  geometry: ShapeGeometry
  /** 塗り (null = なし) */
  fillHex: string | null
  fillAlpha: number
  /** 線 (null = なし) */
  strokeHex: string | null
  strokeWidth: number
  strokeAlpha: number
  strokeAlign: 'center' | 'inside' | 'outside'
  /** 幾何 (文書座標) に掛ける変換 */
  transform: Mat3
}

/**
 * スマートオブジェクト: 元画像 (不変の表面) を保持し、変換行列だけを持つ非破壊レイヤー。
 * 表示は元画像を行列で再サンプルしたラスタキャッシュ。自由変形は行列に合成する。
 */
export interface SmartLayer extends LayerBase {
  kind: 'smart'
  blendMode: BlendMode
  /** 元画像の表面 (変更しない。複製時は共有する) */
  sourceId: PixelId
  /** 元画像の矩形 (表面座標) */
  sourceRect: Rect
  /** 元画像座標 → 文書座標 */
  transform: Mat3
  interpolation: 'nearest' | 'bilinear' | 'bicubic'
}

export type Layer = RasterLayer | GroupLayer | AdjustmentLayer | FillLayer | TextLayer | ShapeLayer | SmartLayer
export type LayerKind = Layer['kind']

export type DocumentBackground = 'transparent' | 'white' | 'black' | { hex: string }

export interface DocumentMeta {
  id: string
  name: string
  createdAt: number
  updatedAt: number
  width: number
  height: number
  dpi: number
  colorProfile: 'sRGB'
  background: DocumentBackground
}

export interface Guide {
  id: string
  axis: 'x' | 'y'
  position: number
}

export interface SelectionState {
  /** 1ch の PixelSurface。null なら選択なし */
  pixelId: PixelId | null
}

/** パターン (タイル画像)。表面は不変で、塗りつぶしレイヤー / パターンオーバーレイ / 塗りつぶしから参照する */
export interface Pattern {
  id: string
  name: string
  pixelId: PixelId
  w: number
  h: number
}

/** レイヤーカンプ: 各レイヤーの表示 / 位置 / スタイルの組み合わせを名前付きで保存する */
export interface LayerCompState {
  visible: boolean
  /** raster: offset、text: position、shape / smart: 行列の平行移動成分 */
  position?: Pt
  styles?: LayerStyle | null
}
export interface LayerComp {
  id: string
  name: string
  states: Record<LayerId, LayerCompState>
}

/** 保存した選択範囲 (アルファチャンネル) */
export interface AlphaChannel {
  id: string
  name: string
  pixelId: PixelId
}

export interface Document {
  schemaVersion: 1
  meta: DocumentMeta
  /** フラットな辞書。親子関係は GroupLayer.children と rootChildren で表す */
  layers: Record<LayerId, Layer>
  /** index 0 が最下層 */
  rootChildren: LayerId[]
  /** 選択中レイヤー。履歴の対象外だが PSD 互換のため文書に持つ */
  activeLayerIds: LayerId[]
  selection: SelectionState
  channels: AlphaChannel[]
  patterns: Pattern[]
  comps: LayerComp[]
  guides: Guide[]
  /** ピクセルが変わるたびに +1。内容署名に含めて未保存検知に使う */
  pixelGeneration: number
}

/** 文書サイズの上限 (長辺)。WebGL の MAX_TEXTURE_SIZE とも突き合わせる */
export const MAX_DOCUMENT_DIMENSION = 16384

/**
 * 型レベルで「JSON にできる型だけ」を強制する。
 * Document が Blob や関数を含むと型エラーになる。
 */
type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonValue[] | { [k: string]: JsonValue }
type IsJson<T> = T extends JsonPrimitive
  ? true
  : T extends (infer U)[]
    ? IsJson<U>
    : T extends object
      ? { [K in keyof T]-?: IsJson<Exclude<T[K], undefined>> }[keyof T] extends true
        ? true
        : false
      : false
export type AssertJson<T> = IsJson<T> extends true ? T : never
// Document が JSON でなくなったらここでコンパイルエラーになる
const _assertDocumentIsJson: AssertJson<Document> = null as unknown as Document
void _assertDocumentIsJson
void (null as unknown as JsonValue)
